import React, { useState, ChangeEvent, FormEvent, useEffect } from 'react';
import InputField from '../common/InputField';
import Button from '../common/Button';
import SideDrawer from '../common/SideDrawer';
import { handleKeyDown, validateFieldWithSchema } from '../../utils/util';
import { z } from "zod";
import { IsFieldValid } from '../../shared/interfaces';
import apiService from '../../services/ApiService';

interface QuickAddPartyFormData {
  partyCode: string;
  partyName: string;
  openingBalance: string;
  harCut: string;
}

interface QuickAddPartyProps {
  isOpen: boolean;
  onClose: () => void;
  onSaveSuccess?: () => void; // Optional callback to refresh parent list
}

const QuickAddPartyDialog: React.FC<QuickAddPartyProps> = ({ isOpen, onClose, onSaveSuccess }) => {
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  
  const initialFormData: QuickAddPartyFormData = {
    partyCode: '',
    partyName: '',
    openingBalance: '0',
    harCut: '0',
  };
  
  const [formData, setFormData] = useState<QuickAddPartyFormData>(initialFormData);

  interface FieldsBoolean {
    partyCode: boolean;
    partyName: boolean;
    openingBalance: boolean;
    harCut: boolean;
  }

  interface FieldsIsValid {
    partyCode: IsFieldValid;
    partyName: IsFieldValid;
    openingBalance: IsFieldValid;
    harCut: IsFieldValid;
    [key: string]: any; // Add index signature to allow string keys
  }

  const [dirtyFields, setDirtyFields] = useState<FieldsBoolean>({
    partyCode: false,
    partyName: false,
    openingBalance: false,
    harCut: false,
  });

  const [isFieldValid, setIsFieldValid] = useState<FieldsIsValid>({
    partyCode: { isValid: true, message: "" },
    partyName: { isValid: true, message: "" },
    openingBalance: { isValid: true, message: "" },
    harCut: { isValid: true, message: "" },
  });

  // Validation schema - same as CreateParty
  const partyCodeValidation = z
    .string({
      required_error: "Party code is required"
    })
    .min(1, "Party code must be 1 characters")
    .max(5, "Party code must be 5 characters")
    .regex(/^[a-zA-Z0-9]+$/, "Party code must be alphanumeric");

  const quickAddPartySchema = z.object({
    partyCode: partyCodeValidation,
    partyName: z.string({ required_error: "Party name is required" }).min(1, "Party name must be at least 1 character").max(60),
    openingBalance: z.coerce.number().min(-99999999999).max(99999999999),
    harCut: z.coerce.number().min(0).max(99999),
  });

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    // Ensure openingBalance is numeric (allow digits and negative sign)
    let processedValue = value;
    if (name === 'openingBalance' && value !== '') {
      // Allow digits and minus sign (only at the start)
      // Remove any characters that aren't digits or minus sign
      processedValue = value.replace(/[^0-9-]/g, '');
      // Ensure minus sign is only at the start
      if (processedValue.includes('-')) {
        const minusIndex = processedValue.indexOf('-');
        if (minusIndex !== 0) {
          // Remove minus sign if it's not at the start
          processedValue = processedValue.replace(/-/g, '');
        } else {
          // Keep minus at start, remove any other minus signs
          processedValue = '-' + processedValue.substring(1).replace(/-/g, '');
        }
      }
      // If empty after processing or just minus sign, set to '0'
      if (processedValue === '' || processedValue === '-') {
        processedValue = '0';
      }
    } else if (name === 'openingBalance' && value === '') {
      processedValue = '0';
    } else if (name === 'harCut') {
      processedValue = value.replace(/\D/g, '').slice(0, 5);
      if (processedValue === '') {
        processedValue = '0';
      }
    }

    setFormData((prev) => ({
      ...prev,
      [name]: processedValue
    }));

    // Mark the field as dirty
    setDirtyFields((prev) => ({
      ...prev,
      [name]: true
    }));

    // Validate the field using zod
    const { isValid, message } = validateFieldWithSchema(
      name,
      processedValue,
      formData,
      quickAddPartySchema
    );

    setIsFieldValid((prev) => ({
      ...prev,
      [name]: { isValid, message }
    }));
  };

  const handleBlur = (e: ChangeEvent<HTMLInputElement>) => {
    const { name } = e.target;
    setDirtyFields((prev) => ({
      ...prev,
      [name]: true
    }));
  };

  const handleOpeningBalanceKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      // Focus on Save Party button
      const saveButton = document.getElementById('quickAddSaveButton') as HTMLButtonElement;
      saveButton?.focus();
    }
  };

  const validateAllFields = (formData: QuickAddPartyFormData) => {
    const result = quickAddPartySchema.safeParse(formData);
    let fieldValid: Partial<FieldsIsValid> = {};

    if (!result.success) {
      for (const issue of result.error.issues) {
        fieldValid[issue.path[0] as string] = {
          isValid: false,
          message: issue.message,
        };
      }
    }

    setIsFieldValid((prev) => ({
      ...prev,
      ...fieldValid,
    }));
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();

    // Validate form fields
    const validation = quickAddPartySchema.safeParse(formData);

    if (!validation.success) {
      validateAllFields(formData);
      // Mark all fields as dirty to show errors
      setDirtyFields({
        partyCode: true,
        partyName: true,
        openingBalance: true,
        harCut: true,
      });
      return;
    }

    setIsProcessing(true);
    
    // Prepare the request payload - same structure as CreateParty
    // Convert partyCode and partyName to uppercase before submitting
    const requestPayload = {
      partyCode: formData.partyCode?.toUpperCase().trim() || '',
      partyName: formData.partyName?.toUpperCase().trim() || '',
      city: '',
      remarks: '',
      phone: '',
      openingBalance: Number(formData.openingBalance),
      vatav: 0,
      harCut: Number(formData.harCut),
      partners: [],
      thirdPartyId: null,
      sessionPartyCommission: 0,
      sessionThirdPartyCommission: 0,
      oneDayPartyCommission: 0,
      oneDayThirdPartyCommission: 0,
      testPartyCommission: 0,
      testThirdPartyCommission: 0,
    };

    // Call POST API - same as CreateParty
    apiService.post('/party', requestPayload)
      .then(async res => {
        setIsProcessing(false);
        onClose(); // Close the dialog after successful submission
        // Call the refresh callback to update parent list if needed
        if (onSaveSuccess) {
          onSaveSuccess();
        }
        // Show success message
        window.dispatchEvent(new CustomEvent('show-toast', { 
          detail: { message: 'Party created successfully!', type: 'success' } 
        }));
        // Dispatch event to refresh party lists (for GameBoard autocomplete, etc.)
        window.dispatchEvent(new CustomEvent('party-created'));
        // Reset form
        setFormData(initialFormData);
        setDirtyFields({
          partyCode: false,
          partyName: false,
          openingBalance: false,
          harCut: false,
        });
        setIsFieldValid({
          partyCode: { isValid: true, message: "" },
          partyName: { isValid: true, message: "" },
          openingBalance: { isValid: true, message: "" },
          harCut: { isValid: true, message: "" },
        });
      })
      .catch(err => {
        setIsProcessing(false);
        // Extract error message from API response
        // API returns { message: "..." } or error.response.data
        const apiErrorMessage = err?.message || err?.error || err?.response?.data?.message;
        const errorMessage = apiErrorMessage || 'Failed to create party';
        
        // Show error toast with the actual API error message
        window.dispatchEvent(new CustomEvent('show-toast', { 
          detail: { message: errorMessage, type: 'error' } 
        }));
        
        // Don't throw error - it's already handled by showing the toast
        // This prevents uncaught runtime errors
      });
  };

  // Reset form when drawer closes
  useEffect(() => {
    if (!isOpen) {
      setFormData(initialFormData);
      setDirtyFields({
        partyCode: false,
        partyName: false,
        openingBalance: false,
        harCut: false,
      });
      setIsFieldValid({
        partyCode: { isValid: true, message: "" },
        partyName: { isValid: true, message: "" },
        openingBalance: { isValid: true, message: "" },
        harCut: { isValid: true, message: "" },
      });
    } else {
      // Focus on first field when drawer opens
      const focusTimer = setTimeout(() => {
        const partyCodeInput = document.getElementById('quickPartyCode') as HTMLInputElement;
        partyCodeInput?.focus();
      }, 350);
      
      return () => clearTimeout(focusTimer);
    }
  }, [isOpen]);

  return (
    <SideDrawer isOpen={isOpen} onClose={onClose} title="Quick Add Party" width="w-[500px]">
      <form onSubmit={handleSubmit} className="space-y-2">
        <InputField 
          label="Party Code" 
          labelClassName="text-gray-700"
          onKeyDown={handleKeyDown} 
          maxLength={5} 
          name="partyCode" 
          id="quickPartyCode"
          value={formData.partyCode} 
          onChange={handleChange} 
          onBlur={handleBlur} 
          className={`${!isFieldValid.partyCode.isValid && dirtyFields.partyCode ? "border-2 border-red-600" : ""}`} 
          error={isFieldValid.partyCode.message} 
        />
        
        <InputField 
          label="Party Name" 
          labelClassName="text-gray-700"
          onKeyDown={handleKeyDown} 
          maxLength={60} 
          name="partyName" 
          value={formData.partyName} 
          onChange={handleChange} 
          onBlur={handleBlur}
          className={!isFieldValid.partyName.isValid && dirtyFields.partyName ? "border-2 border-red-600" : ""} 
          error={isFieldValid.partyName.message} 
        />
        
        <InputField 
          label="Opening Balance" 
          labelClassName="text-gray-700" 
          onKeyDown={handleOpeningBalanceKeyDown} 
          min={-99999999999} 
          max={99999999999} 
          maxLength={12} 
          type='text' 
          name="openingBalance" 
          value={formData.openingBalance} 
          onChange={handleChange} 
          onBlur={handleBlur}
          className={!isFieldValid.openingBalance.isValid && dirtyFields.openingBalance ? "border-2 border-red-600" : ""} 
          error={isFieldValid.openingBalance.message} 
        />

        <InputField
          label="HarCut (0–99999)"
          labelClassName="text-gray-700"
          onKeyDown={handleKeyDown}
          min={0}
          max={99999}
          maxLength={5}
          type="text"
          inputMode="numeric"
          name="harCut"
          value={formData.harCut}
          onChange={handleChange}
          onBlur={handleBlur}
          className={!isFieldValid.harCut.isValid && dirtyFields.harCut ? "border-2 border-red-600" : ""}
          error={isFieldValid.harCut.message}
        />

        <div className="flex justify-end space-x-2 pt-2 mt-3 border-t border-gray-200">
          <Button
            type="button"
            onClick={onClose}
            className="bg-gray-400 hover:bg-gray-500 text-white px-4 py-2 rounded-md"
          >
            Cancel
          </Button>
          <Button 
            id="quickAddSaveButton"
            type="submit"
            isProcessing={isProcessing}
            className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Save Party
          </Button>
        </div>
      </form>
    </SideDrawer>
  );
};

export default QuickAddPartyDialog;

